'use client';
import { Typography } from '@curology/ui-components-web/react/Typography';
import { useAtom } from 'jotai';
import React from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCheckout } from '@/components/flow/steps/checkout/hooks/useCheckout';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import { useCheckoutMode } from '@/components/product-selection/hooks/useCheckoutMode';
import { Button } from '@/components/squeeze';
import { INVALID_NAME_ERROR_MESSAGE } from '@/consts/messages';
import { ALL_STATES } from '@/consts/states';
import { NAME_REGEX, ZIP_CODE_REGEX } from '@/consts/validation';
import { renderFlashMessageOnCheckoutSuccess } from '@/helpers/productSelectionAndCheckout/helpers';
import {
  type Patient,
  type ShippingAddressFields,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

import ShippingAddressAutosuggestTextInput from './shippingAddressAutosuggestTextInput';
import ShippingAddressSelectInput from './shippingAddressSelectInput';
import ShippingAddressTextInput from './shippingAddressTextInput';
import { CHECKOUT_STATES } from '../consts/checkoutStates';
import {
  updateShippingAddress,
  updateShippingAddressAndActivateSubscription,
} from '../js/updateShippingAddressAndActivateTrial';

const ShippingAddressForm: React.FC<{
  checkoutState: string;
  futureShipmentTotal: number;
  patient: Patient;
  setPatient: React.Dispatch<React.SetStateAction<Patient>>;
  setShippingAddressFields: React.Dispatch<
    React.SetStateAction<ShippingAddressFields>
  >;
  setSubmittingForm: React.Dispatch<React.SetStateAction<boolean>>;
  shippingAddressFields: ShippingAddressFields;
  showFlashMessageOnCheckoutSuccess?: boolean;
  submittingForm: boolean;
  onUpdate?: () => void;
  agencySmallBottlesInSignUp?: boolean;
}> = ({
  checkoutState,
  patient,
  setPatient,
  setShippingAddressFields,
  setSubmittingForm,
  shippingAddressFields,
  showFlashMessageOnCheckoutSuccess,
  submittingForm,
  onUpdate,
  agencySmallBottlesInSignUp,
}) => {
  const checkoutMode = useCheckoutMode();
  const { brand, treatmentCategory } = useFlowContext();
  const { onSuccess, onIneligible } = useCheckout();
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  if (checkoutState !== CHECKOUT_STATES.shippingAddressEntry) {
    return null;
  }

  const requiredFieldsProvidedAndValid =
    NAME_REGEX.test(shippingAddressFields.firstName.trim()) &&
    NAME_REGEX.test(shippingAddressFields.lastName.trim()) &&
    shippingAddressFields.streetAddress.trim() !== '' &&
    shippingAddressFields.city.trim() !== '' &&
    shippingAddressFields.state.trim() !== '' &&
    shippingAddressFields.zipCode.trim() !== '' &&
    ZIP_CODE_REGEX.test(shippingAddressFields.zipCode);

  const submitButtonDisabled =
    !requiredFieldsProvidedAndValid || submittingForm;

  const legalNameOnBlurAction = (id: string) => {
    const name = id;
    const value = shippingAddressFields[id];

    if (!NAME_REGEX.test(value)) {
      trackEvent(AnalyticsEvent.INVALID_VALUE_ENTERED, {
        name,
        value,
      });
    }
  };

  const onSubmit = async () => {
    setSubmittingForm(true);

    if (onUpdate) {
      const addressUpdated = await updateShippingAddress(
        flashMessages,
        setFlashMessages,
        setPatient,
        shippingAddressFields,
        treatmentCategory,
        brand,
        onIneligible,
      );

      if (addressUpdated) {
        onUpdate();
      }
    } else {
      const success = await updateShippingAddressAndActivateSubscription(
        brand,
        flashMessages,
        patient,
        setFlashMessages,
        setPatient,
        setSubmittingForm,
        shippingAddressFields,
        treatmentCategory,
        checkoutMode === CHECKOUT_MODES.Trial,
        onIneligible,
        agencySmallBottlesInSignUp,
      );

      if (!success) {
        return;
      }

      if (showFlashMessageOnCheckoutSuccess) {
        renderFlashMessageOnCheckoutSuccess(flashMessages, setFlashMessages);
      }

      onSuccess();
    }
  };

  return (
    <div className="cur-bg-surface-neutral cur-mt-4 cur-p-4">
      <Typography size="subhead">Shipping address</Typography>
      <div className="cur-mt-4">
        <ShippingAddressTextInput
          fieldEnteredFieldName="checkout.firstname"
          fieldEnteredOrderNumber={1}
          format={NAME_REGEX}
          formatErrorMessage={INVALID_NAME_ERROR_MESSAGE}
          id="firstName"
          label="First name"
          onBlurAction={legalNameOnBlurAction}
          placeholder="Jane"
          required
          setShippingAddressFields={setShippingAddressFields}
          shippingAddressFields={shippingAddressFields}
        />
      </div>
      <div className="cur-mt-4">
        <ShippingAddressTextInput
          fieldEnteredFieldName="checkout.lastname"
          fieldEnteredOrderNumber={2}
          format={NAME_REGEX}
          formatErrorMessage={INVALID_NAME_ERROR_MESSAGE}
          id="lastName"
          label="Last name"
          onBlurAction={legalNameOnBlurAction}
          placeholder="Doe"
          required
          setShippingAddressFields={setShippingAddressFields}
          shippingAddressFields={shippingAddressFields}
        />
      </div>
      <div className="cur-mt-4">
        <ShippingAddressAutosuggestTextInput
          fieldEnteredFieldName="checkout.address1"
          fieldEnteredOrderNumber={3}
          id="streetAddress"
          label="Street address"
          placeholder="1234 Main Street"
          required
          setShippingAddressFields={setShippingAddressFields}
          shippingAddressFields={shippingAddressFields}
        />
      </div>
      <div className="cur-mt-4">
        <ShippingAddressTextInput
          fieldEnteredFieldName="checkout.address2"
          fieldEnteredOrderNumber={4}
          id="apartmentSuite"
          label="Apartment, suite, etc."
          placeholder="Apt 100"
          setShippingAddressFields={setShippingAddressFields}
          shippingAddressFields={shippingAddressFields}
        />
      </div>
      <div className="cur-mt-4">
        <ShippingAddressTextInput
          fieldEnteredFieldName="checkout.city"
          fieldEnteredOrderNumber={5}
          id="city"
          label="City"
          placeholder="San Francisco"
          required
          setShippingAddressFields={setShippingAddressFields}
          shippingAddressFields={shippingAddressFields}
        />
      </div>
      <div className="cur-flex cur-mt-4">
        <div className="cur-flex-1 cur-pr-2">
          <ShippingAddressSelectInput
            fieldEnteredFieldName="checkout.state"
            fieldEnteredOrderNumber={6}
            id="state"
            label="State"
            options={ALL_STATES}
            required
            setShippingAddressFields={setShippingAddressFields}
            shippingAddressFields={shippingAddressFields}
          />
        </div>
        <div className="cur-flex-1 cur-pl-2">
          <ShippingAddressTextInput
            fieldEnteredFieldName="checkout.zipcode"
            fieldEnteredOrderNumber={7}
            format={ZIP_CODE_REGEX}
            id="zipCode"
            label="Zip code"
            placeholder="92104"
            required
            setShippingAddressFields={setShippingAddressFields}
            shippingAddressFields={shippingAddressFields}
          />
        </div>
      </div>
      <div className="cur-mt-4">
        <Button
          className="cur-w-full"
          data-sentry-id="Update shipping address and activate trial"
          loading={submittingForm}
          disabled={submitButtonDisabled}
          onClick={onSubmit}
          size="lg"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ShippingAddressForm;
